'use client';

import { getFaro, isFullRumEnabled } from '@/monitoring/client/faro';
import type {
  AnalyticsAdapter,
  ChangeDateFilterProps,
  ChangeResolutionProps,
  ChangeTablePageSizeProps,
  DownloadInvoiceProps,
  ExportProps,
  PageViewProps,
  PinTableColumnProps,
  SearchTableContentsProps,
  SortTableColumnsProps,
  UpdatePropertySelectionProps,
  UserProps,
} from '../types';

/**
 * Factory function that creates a Faro adapter.
 *
 * Uses Faro's UserAction API for interactions and pushEvent for completed
 * milestones that must not start another action during ongoing activity.
 *
 * FaroProvider owns the full-RUM gate. Until it enables full RUM, every adapter
 * method is a no-op, including user identity and event tracking. Callers must
 * not treat these events as always-on. AnalyticsInitializer does not evaluate
 * this as the only consent check: it explicitly evaluates Analytics consent to
 * gate identity and page-view tracking, then passes that state to
 * usePageViewTracking. The initializer prevents those calls before they reach
 * the tracker; this adapter guard independently prevents any Faro call before
 * full RUM is enabled.
 */
export const createFaroAdapter = (): AnalyticsAdapter => {
  return {
    setUser({ id, email }: UserProps): void {
      if (!isFullRumEnabled()) return;
      getFaro()?.api.setUser({ id, email });
    },

    resetUser(): void {
      const faro = getFaro();
      faro?.api.resetUser();
      faro?.api.resetSession();
    },

    trackLogin(): void {
      if (!isFullRumEnabled()) return;
      getFaro()?.api.pushEvent('user_login', { event_category: 'user' });
    },

    trackPageView({ pagePath, pageTitle, locale }: PageViewProps): void {
      if (!isFullRumEnabled()) return;
      const faro = getFaro();
      if (!faro) return;

      faro.api.setView({ name: pagePath });
      faro.api.startUserAction('page_view', {
        event_category: 'navigation',
        page_path: pagePath,
        page_title: pageTitle ?? '',
        locale: locale ?? '',
      });
    },

    trackExport({ exportType, success, stage }: ExportProps): void {
      if (!isFullRumEnabled()) return;
      // The raw error message is intentionally NOT sent to always-on RUM (it can
      // carry internal/server detail). Only low-cardinality outcome fields are attached here.
      getFaro()?.api.startUserAction('data_export', {
        event_category: 'export',
        export_type: exportType,
        event_outcome: success ? 'success' : 'failure',
        stage,
      });
    },

    trackChangeDateFilter(props: ChangeDateFilterProps): void {
      if (!isFullRumEnabled()) return;
      getFaro()?.api.startUserAction('date_filter_changed', {
        event_category: 'filter',
        from: props.from,
        to: props.to,
        view_type: props.viewType,
        number_of_days: String(props.numberOfDays),
      });
    },

    trackSortTableColumns(props: SortTableColumnsProps): void {
      if (!isFullRumEnabled()) return;
      getFaro()?.api.startUserAction('table_sorted', {
        event_category: 'table',
        table_name: props.tableName,
        columns: props.columns.join(';'),
      });
    },

    trackPinTableColumn(props: PinTableColumnProps): void {
      if (!isFullRumEnabled()) return;
      getFaro()?.api.startUserAction('table_column_pinned', {
        event_category: 'table',
        table_name: props.tableName,
        column: props.column,
      });
    },

    trackChangeTablePageSize(props: ChangeTablePageSizeProps): void {
      if (!isFullRumEnabled()) return;
      getFaro()?.api.startUserAction('table_page_size_changed', {
        event_category: 'table',
        table_name: props.tableName,
        page_size: String(props.newPageSize),
      });
    },

    trackSearchTableContents(props: SearchTableContentsProps): void {
      if (!isFullRumEnabled()) return;
      // Note: the raw search query is intentionally NOT sent to Faro (always-on
      // RUM). Only the low-cardinality table name is attached.
      getFaro()?.api.startUserAction('table_search', {
        event_category: 'search',
        table_name: props.tableName,
      });
    },

    trackUpdatePropertySelection(props: UpdatePropertySelectionProps): void {
      if (!isFullRumEnabled()) return;
      getFaro()?.api.startUserAction('property_selection_updated', {
        event_category: 'filter',
        selected_meters_count: String(props.selectedMetersCount),
        total_meters_count: String(props.totalMetersCount),
        percentage_of_meters_selected: String(props.percentageOfMetersSelected),
      });
    },

    trackSearchInPropertySelector(): void {
      if (!isFullRumEnabled()) return;
      // Raw search query intentionally omitted from always-on RUM (see above).
      getFaro()?.api.startUserAction('property_selector_search', { event_category: 'search' });
    },

    trackDownloadInvoice(props: DownloadInvoiceProps): void {
      if (!isFullRumEnabled()) return;
      // Uses pushEvent (not startUserAction) on purpose. This fires AFTER the
      // invoice PDF fetch has already completed, so there is no trailing
      // network/log activity to anchor a UserAction — startUserAction would
      // self-cancel ~100ms after creation. pushEvent is emitted immediately.
      //
      // The invoice number is a customer business identifier and is
      // intentionally kept out of always-on RUM.
      // The customer id/name and the rolling 24h count ARE sent so dashboards
      // can flag download bursts per customer (e.g. customer_downloads_24h >= 3)
      // and identify the customer at a glance, without needing a separate event.
      getFaro()?.api.pushEvent('invoice_downloaded', {
        event_category: 'export',
        customer_id: props.customerId,
        customer_name: props.customerName,
        customer_downloads_24h: String(props.customerDownloads24h),
      });
    },

    trackChangeResolution(props: ChangeResolutionProps): void {
      if (!isFullRumEnabled()) return;
      getFaro()?.api.startUserAction('resolution_changed', {
        event_category: 'filter',
        resolution: props.resolution,
        page: props.page,
      });
    },
  };
};
